/**
 * A2F-655 / A2F-998 — Document Signoff (top-level Chrona Documents surface;
 * was Plan / Document Readiness).
 *
 * Combined view of two sign-off layers per document:
 *   • Content sign-offs — per section, owners assigned via the Chrona Word add-in.
 *   • Document sign-offs — per document, owners assigned in this tab.
 *
 * Real data sources:
 *   • Users: api.orgs.members(activeOrgId), filtered to active members with a
 *     linked auth user (no mock taxonomy, no cross-org leakage).
 *   • Submissions + documents: api.projects.submissions / api.documents.explorer.
 *   • Section sign-offs (CONTENT layer): api.sectionOwners.get(projectId,
 *     submissionId) — same source Clear's Owner Sign-off tab uses (A2F-662).
 *   • Document sign-offs (DOCUMENT layer): api.documentReadiness.* — backed
 *     by `document_owner_assignments` (A2F-663 MVP). Mutations apply
 *     optimistically and roll back on failure with a toast.
 *
 * Un-gated as of A2F-666 — the dev-mode shortcut (Ctrl+Shift+D) is no
 * longer required. The §11.50 attestation copy + signed_file_hash pinning
 * still ship under A2F-665.
 */

import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { FileText, Check, Clock, Search, Filter, ChevronDown, CircleAlert, RefreshCw, LoaderCircle } from 'lucide-react';
import { api } from '@/lib/api';
import { useProject } from '@/lib/ProjectContext';
import { useAuth } from '@/lib/AuthContext';
import { usePermissions } from '@/lib/usePermissions';
import { useScanStatus } from '@/lib/ScanStatusContext';
import { useSubmissionFocus } from '@/lib/SubmissionFocusContext';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { DocumentReadinessTable } from './DocumentReadinessTable';
import { DocumentReadinessSignOffModal } from './DocumentReadinessSignOffModal';
import { DocumentReadinessEditOwners } from './DocumentReadinessEditOwners';
import {
  contentSignedCounts,
  docSignedCounts,
  matchOwnerNameToUser,
  unifiedStatus,
  type DocumentOwnerEntry,
  type ReadinessRow,
  type SectionSignOff,
  type StatusKind,
} from './documentReadinessHelpers';
import { invalidateDocumentReadiness } from '@/lib/documentReadinessInvalidation';
import type {
  Submission,
  ExplorerDocument,
  UserOut,
  OrgMember,
  SectionOwnerScanResponse,
} from '@/types/api';

/**
 * Invert the per-owner section_owner scan into per-document SectionSignOff
 * lists. Each `SectionOwnerEntry.sections` row carries a `documentId` and
 * a `signedOff` boolean; we group those by `documentId` and resolve each
 * Word-comment author name to an org-member user id where possible
 * (locked rules in `matchOwnerNameToUser`).
 *
 * Per-submission scan path returns only documents physically uploaded in
 * the focused submission, so docs that are lifecycle-resolved into the
 * submission but not uploaded there will simply be absent from the
 * returned map — their `ReadinessRow.sections` stays `[]`. That's the
 * correct denominator behavior (no contribution to numerator OR
 * denominator of % Content signed off) per the A2F-662 spec.
 */
function buildSectionsByDoc(
  scan: SectionOwnerScanResponse,
  users: ReadonlyArray<UserOut>,
): Map<string, SectionSignOff[]> {
  const out = new Map<string, SectionSignOff[]>();
  for (const owner of scan.owners) {
    for (const s of owner.sections) {
      const ownerName = owner.ownerName;
      const ownerUserId = matchOwnerNameToUser(ownerName, users);
      // SectionDetail.anchorText is the Word heading the comment is anchored
      // to — typically prose like "Effects in Humans", occasionally a numeric
      // section identifier. Treat it as the title and leave `num` empty
      // rather than misrepresent prose as a section number. The card UI
      // skips an empty `num` cleanly.
      const entry: SectionSignOff = {
        id: s.commentId,
        num: '',
        title: s.anchorText || s.document,
        ownerUserId,
        ownerName,
        state: s.signedOff ? 'signed' : 'pending',
      };
      const arr = out.get(s.documentId) ?? [];
      arr.push(entry);
      out.set(s.documentId, arr);
    }
  }
  return out;
}

/**
 * Map an active OrgMember to the UserOut shape consumed downstream by the
 * picker, avatar, and owner cards. Returns null when the member can't be a
 * sign-off owner — invited but not yet active, deactivated, or no linked
 * auth user (so we'd have nothing to persist as the foreign key).
 */
function orgMemberToUser(m: OrgMember): UserOut | null {
  if (m.status !== 'active') return null;
  if (!m.userId) return null;
  return {
    id: m.userId,
    email: m.email ?? '',
    displayName: m.displayName ?? m.email ?? 'Unknown',
    role: m.role,
    isActive: true,
    oauthProvider: m.authMethod,
    createdAt: '',
    lastLoginAt: m.lastLoginAt,
  };
}

type StatusFilter = 'all' | StatusKind;

const STATUS_FILTER_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All statuses' },
  { value: 'progress', label: 'In progress' },
  { value: 'pending', label: 'Awaiting document sign-off' },
  { value: 'warn', label: 'Signed off — content open' },
  { value: 'signed', label: 'Signed off' },
  { value: 'unassigned', label: 'No document owners' },
];

function StatTile({
  icon,
  label,
  value,
  foot,
  tone,
  active,
  onClick,
  testId,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  foot?: string;
  tone?: 'green' | 'amber' | 'red';
  active?: boolean;
  onClick?: () => void;
  /** data-testid for E2E selectors — see tests/e2e/sub9.documentReadiness.spec.ts */
  testId?: string;
}) {
  const numCls =
    tone === 'green'
      ? 'text-emerald-600'
      : tone === 'amber'
        ? 'text-amber-600'
        : tone === 'red'
          ? 'text-red-600'
          : 'text-brand-charcoal';
  const wrapCls = `flex flex-col gap-1 rounded-xl border bg-white px-4 py-3 ${
    active
      ? 'border-brand-orange shadow-[0_0_0_3px_rgba(236,91,19,0.10)]'
      : onClick
        ? 'border-slate-200 hover:border-slate-300 cursor-pointer'
        : 'border-slate-200'
  }`;
  return (
    <div className={wrapCls} onClick={onClick} data-testid={testId}>
      <div className="flex items-center gap-1.5 text-[11px] text-slate-500">
        {icon}
        {label}
      </div>
      <div className={`text-2xl font-bold leading-tight ${numCls}`}>{value}</div>
      {foot && <div className="text-[11px] text-slate-400">{foot}</div>}
    </div>
  );
}

function SubmissionPill({
  sub,
  active,
  onClick,
}: {
  sub: Submission;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider transition-colors ${
        active
          ? 'bg-brand-orange text-white shadow-[0_0_0_3px_rgba(236,91,19,0.10)]'
          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
      }`}
    >
      Sub {sub.displaySequence}
    </button>
  );
}

/**
 * Plan-side Rescan affordance. Triggers `api.sectionOwners.scan` for the
 * focused submission — same backend the Document Clear tab's Rescan calls,
 * so the two tabs share scan state via `useScanStatus('section-owners')`
 * and observe each other's invalidations. Real backend split was rejected
 * in PR scoping (file I/O dominates; per-tab targeting risks staleness).
 */
function RescanButton({
  projectId,
  submissionId,
  onScanned,
  onError,
}: {
  projectId: string;
  submissionId: string | null;
  onScanned: (scan: SectionOwnerScanResponse) => void;
  onError: (msg: string) => void;
}) {
  const { canRunScans } = usePermissions();
  const scanStatus = useScanStatus();
  const busy = scanStatus.isScanning('section-owners');

  const handleClick = useCallback(() => {
    if (!projectId || !submissionId) return;
    if (!scanStatus.startScan('section-owners')) return;
    api.sectionOwners
      .scan(projectId, undefined, submissionId)
      .then((scan) => {
        onScanned(scan);
      })
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : 'Rescan failed.';
        onError(msg);
      })
      .finally(() => {
        scanStatus.endScan('section-owners');
      });
  }, [projectId, submissionId, scanStatus, onScanned, onError]);

  const disabled = !canRunScans || busy || !submissionId;
  const title = !canRunScans
    ? 'Requires Contributor role'
    : busy
      ? 'Scan in progress'
      : !submissionId
        ? 'Pick an active submission first'
        : 'Re-read [CHRONA:OWNER:…] comments + their resolved status from Word';

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      title={title}
      className={`inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50 ${
        disabled ? 'cursor-not-allowed opacity-50' : ''
      }`}
    >
      {busy ? (
        <LoaderCircle className="h-3.5 w-3.5 animate-spin" />
      ) : (
        <RefreshCw className="h-3.5 w-3.5" />
      )}
      {busy ? 'Scanning…' : 'Rescan'}
    </button>
  );
}

export function DocumentReadinessPage() {
  const { activeProject } = useProject();
  const { user } = useAuth();
  const { role, canReadUsers } = usePermissions();
  const projectId = activeProject?.id ?? '';

  const canEdit = role === 'admin' || role === 'lead';
  const currentUserId = user?.id ?? null;

  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [users, setUsers] = useState<UserOut[]>([]);
  const [docsBySubmission, setDocsBySubmission] = useState<Map<string, ReadinessRow[]>>(new Map());
  const [loading, setLoading] = useState(true);
  const [submissionId, setSubmissionId] = useState<string | null>(null);

  // A2F-760: capture the global submission focus once on mount so a
  // deep-link from the Gantt (% Content Done / % Document Done) lands
  // on the sub the user clicked rather than planning[0]. The ref is
  // consumed on first effect run; later project switches and later
  // global-focus changes do not override the page-local pill — the
  // page-local-not-global-focus design contract still holds for
  // everything except the initial deep-link.
  const { submissionFocusId } = useSubmissionFocus();
  const initialFocusRef = useRef<string | null>(submissionFocusId);

  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [pendingMine, setPendingMine] = useState(false);

  const [signOffDocId, setSignOffDocId] = useState<string | null>(null);
  const [editDocId, setEditDocId] = useState<string | null>(null);
  const [toast, setToast] = useState<string>('');

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  }, []);

  // Project-scoped initial load: submissions, documents, users.
  //
  // Source of truth for "users" is the active org's members — same endpoint
  // Settings → Manage users uses (api.orgs.members). PR #205 review.
  //
  // Section sign-offs are NOT loaded here — they're per-submission and
  // fetched in the second effect below when `submissionId` resolves.
  // Documents start with `sections: []` until that fetch lands.
  //
  // allSettled so any one promise failing doesn't blow away the others;
  // skip the members fetch entirely for roles that can't read /users (the
  // endpoint also requires lead+, mirroring the role check).
  const activeOrgId = user?.activeOrgId ?? user?.orgId ?? null;
  useEffect(() => {
    if (!projectId) return;
    // Reset selection synchronously on project change — prevents the
    // per-submission effects below (sections, docOwners) from firing with
    // a stale (B project, A submissionId) pair while the new submissions
    // list is in flight.
    setSubmissionId(null);
    let cancelled = false;
    setLoading(true);
    const membersPromise: Promise<OrgMember[]> =
      canReadUsers && activeOrgId
        ? api.orgs.members(activeOrgId)
        : Promise.resolve([]);
    Promise.allSettled([
      api.projects.submissions(projectId, true),
      api.documents.explorer(projectId),
      membersPromise,
    ])
      .then(([subsR, explorerR, membersR]) => {
        if (cancelled) return;
        const subs = subsR.status === 'fulfilled' ? subsR.value : [];
        const explorer = explorerR.status === 'fulfilled' ? explorerR.value : null;
        const members = membersR.status === 'fulfilled' ? membersR.value : [];
        const userList = members
          .map(orgMemberToUser)
          .filter((u): u is UserOut => u !== null);

        const planning = subs.filter((s) => s.isPlanning);
        setSubmissions(planning);
        setUsers(userList);
        if (planning.length > 0) {
          const focused = initialFocusRef.current;
          initialFocusRef.current = null;
          const preferredId =
            focused && planning.some((s) => s.id === focused)
              ? focused
              : planning[0].id;
          setSubmissionId(preferredId);
        } else {
          setSubmissionId(null);
        }

        const map = new Map<string, ReadinessRow[]>();
        if (explorer) {
          for (const d of explorer.documents as ExplorerDocument[]) {
            const row: ReadinessRow = {
              id: d.id,
              section: d.ectdSection,
              name: d.fileName.replace(/\.(docx|pdf|doc)$/i, ''),
              docOwners: [],
              sections: [],
            };
            const arr = map.get(d.submissionId) ?? [];
            arr.push(row);
            map.set(d.submissionId, arr);
          }
        }
        setDocsBySubmission(map);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, canReadUsers, activeOrgId]);

  // Per-submission section sign-offs (A2F-662).
  //
  // Source: api.sectionOwners.get(projectId, submissionId), the same scan
  // Chrona Clear → Owner Sign-off renders. Per-submission path filters by
  // direct equality on Document.submission_id and never returns inherited
  // sections — see backend/services/section_owner_service.py:58-85 (the
  // `if submission_id` branch). This is intentional: Document Readiness
  // metrics describe the submission's own work, not state inherited from
  // prior subs.
  //
  // Plan-side Rescan now lives in the Submission Pills row (RescanButton
  // below). It triggers the same unified section_owner scan Document Clear
  // uses, so both tabs invalidate + refresh the same cache. We accepted the
  // dual-tab full-scan trade-off in PR #214 over a real backend split.
  const mergeSectionsForSubmission = useCallback(
    (scan: SectionOwnerScanResponse) => {
      if (!submissionId) return;
      const sectionsByDoc = buildSectionsByDoc(scan, users);
      setDocsBySubmission((prev) => {
        const next = new Map(prev);
        const arr = next.get(submissionId);
        if (!arr) return prev;
        next.set(
          submissionId,
          arr.map((row) => ({ ...row, sections: sectionsByDoc.get(row.id) ?? [] })),
        );
        return next;
      });
    },
    [submissionId, users],
  );

  useEffect(() => {
    if (!projectId || !submissionId) return;
    let cancelled = false;
    api.sectionOwners
      .get(projectId, submissionId)
      .then((scan: SectionOwnerScanResponse) => {
        if (cancelled) return;
        mergeSectionsForSubmission(scan);
      })
      .catch(() => {
        // 404 = no scan yet (submission has no .docx files, or scan hasn't
        // run). Leave sections as []; the table will render "—" cells with
        // tooltips per the empty-state convention.
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, submissionId, mergeSectionsForSubmission]);

  // Per-submission docOwners (A2F-663). Hydrate `ReadinessRow.docOwners`
  // from the persisted assignments table.
  useEffect(() => {
    if (!submissionId) return;
    let cancelled = false;
    api.documentReadiness
      .getAssignments(submissionId)
      .then((res) => {
        if (cancelled) return;
        setDocsBySubmission((prev) => {
          const next = new Map(prev);
          const arr = next.get(submissionId);
          if (!arr) return prev;
          next.set(
            submissionId,
            arr.map((row) => {
              const owners = res.assignments[row.id] ?? [];
              return {
                ...row,
                docOwners: owners.map((o) => ({
                  userId: o.userId,
                  state: o.state,
                  comment: o.comment ?? undefined,
                })),
              };
            }),
          );
          return next;
        });
      })
      .catch(() => {
        // On failure leave docOwners untouched (initial seed is []) — the
        // user sees an unassigned state, can retry the action, and the
        // toast surfaces real write failures separately.
      });
    return () => {
      cancelled = true;
    };
  }, [submissionId]);

  const usersById = useMemo(() => {
    const m = new Map<string, UserOut>();
    for (const u of users) m.set(u.id, u);
    return m;
  }, [users]);

  const subDocs = submissionId ? (docsBySubmission.get(submissionId) ?? []) : [];
  const focusedSub = submissions.find((s) => s.id === submissionId) ?? null;

  // Stats for the focused submission.
  //
  // % Content signed off (LOCKED metric formula, A2F-662):
  //   numerator   = count of sections where signedOff === true, across all
  //                 entries returned by api.sectionOwners.get for this
  //                 submission (summed over the focused sub's documents).
  //   denominator = total sections returned by the same scan call.
  //   Documents lifecycle-resolved into the submission but not physically
  //   uploaded there return zero sections from the per-submission scan path,
  //   so they contribute zero to both numerator and denominator. They don't
  //   pull the headline percentage down. (Verified via
  //   backend/services/section_owner_service.py:58-85 — the `if
  //   submission_id` branch bypasses lifecycle resolution.)
  //
  //   Sub 9 expected post-fix: 50% (1/2) — equal to Clear's % Signed Off
  //   on the same submission. Coincidence-equal because each owner has
  //   one section in this fixture; metrics differ structurally and
  //   diverge as soon as one owner has multiple sections.
  //
  // % Document signed off (in-memory; A2F-663 will persist):
  //   numerator   = count of docOwners where state === 'signed' across all
  //                 documents in the focused submission.
  //   denominator = total docOwners assigned across all documents.
  // A2F-666: per-doc-fully-signed formula on both metrics so the Gantt and
  // Doc Readiness numbers always agree. A doc only enters the denominator
  // when it has at least one owner of the relevant kind; the unassigned
  // count tiles below catch the gap a pure percentage would hide.
  const stats = useMemo(() => {
    const total = subDocs.length;
    let docDoneCount = 0;
    let docDoneTotal = 0;
    let contentDoneCount = 0;
    let contentDoneTotal = 0;
    let pendingMineCount = 0;
    let docsUnassigned = 0;
    let docsNoContentOwners = 0;
    for (const d of subDocs) {
      const ds = docSignedCounts(d);
      const cs = contentSignedCounts(d);
      if (ds.total > 0) {
        docDoneTotal += 1;
        if (ds.signed === ds.total) docDoneCount += 1;
      } else {
        docsUnassigned += 1;
      }
      if (cs.total > 0) {
        contentDoneTotal += 1;
        if (cs.signed === cs.total) contentDoneCount += 1;
      } else {
        docsNoContentOwners += 1;
      }
      if (
        currentUserId &&
        d.docOwners.some((o) => o.userId === currentUserId && o.state === 'pending')
      ) {
        pendingMineCount += 1;
      }
    }
    return {
      total,
      pctDoc: docDoneTotal > 0 ? Math.round((100 * docDoneCount) / docDoneTotal) : null,
      docDoneCount,
      docDoneTotal,
      pctContent:
        contentDoneTotal > 0 ? Math.round((100 * contentDoneCount) / contentDoneTotal) : null,
      contentDoneCount,
      contentDoneTotal,
      pendingMine: pendingMineCount,
      docsUnassigned,
      docsNoContentOwners,
    };
  }, [subDocs, currentUserId]);

  // Filter
  const filtered = useMemo(() => {
    return subDocs.filter((d) => {
      if (
        pendingMine &&
        !(currentUserId && d.docOwners.some((o) => o.userId === currentUserId && o.state === 'pending'))
      ) {
        return false;
      }
      if (statusFilter !== 'all') {
        if (unifiedStatus(d).kind !== statusFilter) return false;
      }
      if (search) {
        const q = search.toLowerCase();
        const ownerMatch =
          d.docOwners.some((o) =>
            usersById.get(o.userId)?.displayName.toLowerCase().includes(q),
          ) ||
          d.sections.some((s) =>
            s.ownerUserId
              ? usersById.get(s.ownerUserId)?.displayName.toLowerCase().includes(q)
              : false,
          );
        if (
          !d.name.toLowerCase().includes(q) &&
          !d.section.toLowerCase().includes(q) &&
          !ownerMatch
        ) {
          return false;
        }
      }
      return true;
    });
  }, [subDocs, pendingMine, statusFilter, search, currentUserId, usersById]);

  const toggleExpand = useCallback((docId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(docId)) next.delete(docId);
      else next.add(docId);
      return next;
    });
  }, []);

  const expandAll = () => {
    if (expandedIds.size === filtered.length && filtered.length > 0) {
      setExpandedIds(new Set());
    } else {
      setExpandedIds(new Set(filtered.map((d) => d.id)));
    }
  };

  // A2F-663 — optimistic update + rollback. We apply the new state to
  // `docsBySubmission` immediately so the table reflects the user's intent
  // without waiting for the round-trip; on API failure we restore the
  // pre-mutation snapshot and surface a toast. The MVP "sign off" action
  // is intentionally a single click + free-form comment — A2F-665 will
  // add the §11.50 attestation copy and pin signed_file_hash.
  const handleSignOff = useCallback(
    (comment: string) => {
      if (!submissionId || !signOffDocId || !currentUserId) return;
      const targetDocId = signOffDocId;
      const snapshot = docsBySubmission;
      setDocsBySubmission((prev) => {
        const next = new Map(prev);
        const arr = next.get(submissionId) ?? [];
        next.set(
          submissionId,
          arr.map((d) =>
            d.id === targetDocId
              ? {
                  ...d,
                  docOwners: d.docOwners.map((o) =>
                    o.userId === currentUserId ? { ...o, state: 'signed', comment } : o,
                  ),
                }
              : d,
          ),
        );
        return next;
      });
      setSignOffDocId(null);
      // A2F-758: fire in .then() (after the write lands) so usePlanningData's
      // refetch reads fresh server state — not at the optimistic update.
      api.documentReadiness
        .sign(submissionId, targetDocId, comment)
        .then(() => invalidateDocumentReadiness(projectId))
        .catch(() => {
          setDocsBySubmission(snapshot);
          showToast('Sign-off failed. Please try again.');
        });
    },
    [submissionId, signOffDocId, currentUserId, docsBySubmission, showToast, projectId],
  );

  const handleSaveOwners = useCallback(
    (newOwners: DocumentOwnerEntry[]) => {
      if (!submissionId || !editDocId) return;
      const targetDocId = editDocId;
      const snapshot = docsBySubmission;
      setDocsBySubmission((prev) => {
        const next = new Map(prev);
        const arr = next.get(submissionId) ?? [];
        next.set(
          submissionId,
          arr.map((d) => (d.id === targetDocId ? { ...d, docOwners: newOwners } : d)),
        );
        return next;
      });
      setEditDocId(null);
      // A2F-758: fire in .then() (after the write lands) so usePlanningData's
      // refetch reads fresh server state. This path covers AC #3 (owner
      // removal / clearing a signed owner) — the only UI path that touches
      // owner lists today.
      api.documentReadiness
        .setOwners(submissionId, targetDocId, newOwners.map((o) => o.userId))
        .then(() => invalidateDocumentReadiness(projectId))
        .catch(() => {
          setDocsBySubmission(snapshot);
          showToast('Could not save owners. Please try again.');
        });
    },
    [submissionId, editDocId, docsBySubmission, showToast, projectId],
  );

  const clearAllFilters = () => {
    setStatusFilter('all');
    setPendingMine(false);
    setSearch('');
  };
  const filtersActive = statusFilter !== 'all' || pendingMine || !!search;

  const signOffDoc = subDocs.find((d) => d.id === signOffDocId) ?? null;
  const editDoc = subDocs.find((d) => d.id === editDocId) ?? null;

  if (loading) return <LoadingSpinner message="Loading document signoff..." />;

  if (submissions.length === 0) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white py-16 text-center">
        <FileText className="mx-auto mb-3 h-10 w-10 text-slate-300" />
        <p className="text-sm font-medium text-slate-500">No active submissions</p>
        <p className="mt-1 text-xs text-slate-400">
          Activate submissions for planning to track document readiness.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {toast && (
        <div className="fixed top-4 right-4 z-50 rounded-lg bg-slate-800 px-4 py-3 text-sm text-white shadow-lg animate-fade-in">
          {toast}
        </div>
      )}
      {/* Page header */}
      <div>
        <p className="text-[10px] font-semibold text-brand-orange uppercase tracking-widest">
          Chrona Documents
        </p>
        <h1 className="text-xl font-bold text-brand-charcoal">Document Signoff</h1>
        <p className="mt-1 text-sm text-slate-500">
          See where each document stands across two tracks:{' '}
          <b className="font-semibold text-brand-charcoal">content sign-offs</b> (per section,
          assigned in Word via the Chrona Word add-in) and{' '}
          <b className="font-semibold text-brand-charcoal">document sign-offs</b> (per document,
          assigned here in the Chrona App).
        </p>
      </div>

      {/* Submission pills + Rescan */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">
          Active subs:
        </span>
        {submissions.map((s) => (
          <SubmissionPill
            key={s.id}
            sub={s}
            active={s.id === submissionId}
            onClick={() => setSubmissionId(s.id)}
          />
        ))}
        <div className="ml-auto">
          <RescanButton
            projectId={projectId}
            submissionId={submissionId}
            onScanned={(scan) => mergeSectionsForSubmission(scan)}
            onError={(msg) => showToast(msg)}
          />
        </div>
      </div>

      {/* Stats — % tiles paired with "no one to sign" companions so the
          headline percentage doesn't paper over unassigned docs. Names +
          formula are kept in lockstep with the Gantt's % Content Done /
          % Document Done metrics (A2F-666). */}
      <div className="grid grid-cols-5 gap-3">
        <StatTile
          icon={<Check className="h-3.5 w-3.5" />}
          label="% Document Done"
          value={stats.pctDoc === null ? '—%' : `${stats.pctDoc}%`}
          tone={
            stats.pctDoc === null
              ? undefined
              : stats.pctDoc === 100
                ? 'green'
                : stats.pctDoc === 0
                  ? 'red'
                  : 'amber'
          }
          foot={
            stats.docDoneTotal > 0
              ? `${stats.docDoneCount} of ${stats.docDoneTotal} docs fully signed`
              : 'No assigned docs'
          }
        />
        <StatTile
          icon={<CircleAlert className="h-3.5 w-3.5" />}
          label="Docs without owners"
          value={stats.docsUnassigned}
          tone={stats.docsUnassigned > 0 ? 'red' : undefined}
          foot={
            stats.total > 0 ? `of ${stats.total} document${stats.total === 1 ? '' : 's'}` : undefined
          }
        />
        <StatTile
          icon={<Check className="h-3.5 w-3.5" />}
          label="% Content Done"
          value={stats.pctContent === null ? '—%' : `${stats.pctContent}%`}
          tone={
            stats.pctContent === null
              ? undefined
              : stats.pctContent === 100
                ? 'green'
                : stats.pctContent === 0
                  ? 'red'
                  : 'amber'
          }
          foot={
            stats.contentDoneTotal > 0
              ? `${stats.contentDoneCount} of ${stats.contentDoneTotal} docs fully signed`
              : 'No tracked content'
          }
          testId="stat-content-signed-off"
        />
        <StatTile
          icon={<CircleAlert className="h-3.5 w-3.5" />}
          label="Docs without content owners"
          value={stats.docsNoContentOwners}
          tone={stats.docsNoContentOwners > 0 ? 'red' : undefined}
          foot={
            stats.total > 0 ? `of ${stats.total} document${stats.total === 1 ? '' : 's'}` : undefined
          }
        />
        <StatTile
          icon={<Clock className="h-3.5 w-3.5" />}
          label="Pending on you"
          value={stats.pendingMine}
          tone={stats.pendingMine > 0 ? 'amber' : undefined}
          foot={pendingMine ? 'Filter active — click to clear' : 'Click to filter'}
          active={pendingMine}
          onClick={() => setPendingMine((p) => !p)}
        />
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex max-w-md flex-1 items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2">
          <Search className="h-3.5 w-3.5 text-slate-400" />
          <input
            type="text"
            placeholder="Search documents, sections, or owners..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full border-0 bg-transparent text-sm outline-none placeholder:text-slate-400"
          />
        </div>
        <div className="relative inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1.5">
          <Filter className="h-3.5 w-3.5 text-slate-400" />
          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            Status
          </span>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            className="appearance-none border-0 bg-transparent pr-5 text-xs font-medium text-brand-charcoal outline-none"
          >
            {STATUS_FILTER_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <ChevronDown className="pointer-events-none absolute right-2 h-3 w-3 text-slate-400" />
        </div>
        {filtersActive && (
          <button
            type="button"
            onClick={clearAllFilters}
            className="rounded-md px-2 py-1 text-xs font-medium text-slate-500 hover:bg-slate-100 hover:text-slate-700"
          >
            Clear filters
          </button>
        )}
        <div className="flex-1" />
        <span className="text-xs text-slate-400">
          {filtered.length} of {subDocs.length} documents
        </span>
        <button
          type="button"
          onClick={expandAll}
          className="rounded-md border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50"
        >
          {expandedIds.size === filtered.length && filtered.length > 0 ? 'Collapse all' : 'Expand all'}
        </button>
      </div>

      {/* Table or empty state */}
      {filtered.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white py-12 text-center">
          <FileText className="mx-auto mb-2 h-7 w-7 text-slate-300" />
          <h3 className="text-sm font-semibold text-brand-charcoal">No documents match</h3>
          <p className="mt-1 text-xs text-slate-400">
            Try clearing search, the status filter, or the "Pending on you" filter.
          </p>
          {filtersActive && (
            <button
              type="button"
              onClick={clearAllFilters}
              className="mt-3 text-xs font-semibold text-brand-orange hover:underline"
            >
              Clear all filters
            </button>
          )}
        </div>
      ) : (
        <DocumentReadinessTable
          docs={filtered}
          currentUserId={currentUserId}
          currentUserNameRefs={{
            displayName: user?.displayName ?? null,
            email: user?.email ?? null,
          }}
          usersById={usersById}
          canEdit={canEdit}
          expandedIds={expandedIds}
          onToggleExpand={toggleExpand}
          onSignOff={(docId) => setSignOffDocId(docId)}
          onEditOwners={(docId) => setEditDocId(docId)}
          onOpenDoc={(docId) => {
            api.documents.openLocal(docId).catch(() => {
              showToast('Could not open document.');
            });
          }}
        />
      )}

      <DocumentReadinessSignOffModal
        open={!!signOffDocId}
        doc={signOffDoc}
        userName={user?.displayName ?? 'You'}
        onClose={() => setSignOffDocId(null)}
        onConfirm={handleSignOff}
      />

      <DocumentReadinessEditOwners
        open={!!editDocId}
        doc={editDoc}
        users={users}
        onClose={() => setEditDocId(null)}
        onSave={handleSaveOwners}
      />
    </div>
  );
}
